import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  trackContentClick,
  trackContentPublisher,
} from "@companion-core/shared/app/tracking/dynamicPage";
import {
  getContentComponentMediaDescriptionAlt,
  getContentComponentMediaUrl,
} from "@companion-core/shared/app/Utils/componentHelper";
import { getLocalizedDateFormat } from "@companion-core/shared/app/Utils/datetime";
import { isWeb } from "@companion-core/shared/app/Utils/platform";
import {
  getDynamicComponentId,
  getDynamicComponentVendorId,
} from "@companion-core/shared/app/Utils/tracking";
import { ComponentCardHookProps } from "@companion-core/shared/app/Components/Cards/ComponentCard/ComponentCard.model";
import {
  APICardComponent,
  APIContentComponent,
  APIEditorialComponent,
  APIVendorComponent,
} from "@companion-core/shared/app/Interfaces/API";
import dayjs from "dayjs";
import { useDynamicPageStore } from "@companion-core/shared/app/Store/dynamicPageStore";
import { usePersonalizeField } from "@companion-core/shared/app/Hooks/personalization";

export const useComponentCard = ({
  component,
  pageSlug,
  chapters,
  nbItems,
  position,
  url,
  variant,
  redirectQueryParams,
}: ComponentCardHookProps) => {
  const { t } = useTranslation();
  const { personalizeField } = usePersonalizeField();
  const { setScrollIndex } = useDynamicPageStore((state) => ({
    setScrollIndex: state.setScrollIndex,
  }));
  const isVendorVariant = useMemo(() => variant === "vendor", [variant]);
  const componentId = useMemo(() => component.parent_id, [component]);
  const componentImageUrl = useMemo(
    () => getContentComponentMediaUrl(component, "image"),
    [component]
  );
  const componentImageAlt = useMemo(() => {
    return getContentComponentMediaDescriptionAlt(component, "image");
  }, [component]);
  const componentLogoUrl = useMemo(
    () => (!isVendorVariant ? getContentComponentMediaUrl(component, "logo") : undefined),
    [component]
  );
  const componentLogoAlt = useMemo(
    () =>
      !isVendorVariant ? getContentComponentMediaDescriptionAlt(component, "logo") : undefined,
    [component]
  );

  const componentTitle = useMemo(
    () => personalizeField(component.title) ?? "",
    [component, personalizeField]
  );
  const componentSubtitle = useMemo(() => {
    const subtitle = !isVendorVariant
      ? (component as APICardComponent | APIEditorialComponent).subtitle ||
        (component as APIContentComponent).vendor?.name
      : ((component as APIVendorComponent).google_place?.address?.formatted_address ??
        t("dynamic-content.editorial.content.vendor.online"));
    return personalizeField(subtitle);
  }, [component, isVendorVariant, t, personalizeField]);
  const componentDate = useMemo(
    () =>
      !isVendorVariant && "end_date" in component && component.end_date
        ? `${t("common.component-card.end-date")} ${dayjs(component.end_date).format(
            getLocalizedDateFormat()
          )}`
        : undefined,
    [component]
  );
  const componentDetailUrl = useMemo(
    () =>
      `/${pageSlug ?? "common"}/content-type/${component?.type?.includes("inspiration") ? "inspiration" : component?.content_ct}/details/${component.parent_id}${redirectQueryParams ? `?${redirectQueryParams}` : ""}`,
    [component, pageSlug]
  );

  const onComponentCardVisible = useCallback(() => {
    if (isWeb()) {
      trackContentPublisher({
        pageSlug,
        chapters,
        url,
        contentId: getDynamicComponentId(component),
        positionItem: position,
        nbItems,
        vendorId: getDynamicComponentVendorId(component),
      });
    }
  }, [component, pageSlug, chapters, nbItems, position, url]);

  const onComponentCardVisited = useCallback(() => {
    if (isWeb()) setScrollIndex({ [pageSlug ?? ""]: window.scrollY });
    trackContentClick({
      pageSlug,
      chapters,
      url,
      contentId: getDynamicComponentId(component),
      positionItem: position,
      nbItems,
      vendorId: getDynamicComponentVendorId(component),
    });
  }, [component, pageSlug, chapters, nbItems, position, url]);

  return {
    componentId,
    componentTitle,
    componentSubtitle,
    componentImageUrl,
    componentImageAlt,
    componentLogoUrl,
    componentLogoAlt,
    componentDate,
    componentDetailUrl,
    onComponentCardVisible,
    onComponentCardVisited,
  };
};
